import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
  ScrollArea,
  LoadingOverlay,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import fetchApi from "../Lib/api";
import plusIcon from "../../stories/assets/add.svg";
import Notification from "../Lib/Notification";
import {
  IconChevronRight,
  IconHandGrab,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import FeatureButton from "../../stories/FeatureButton";
import useStartupStore from "../../stores/startupStore";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useUserStore from "../../stores/userStore";
import Mixpanel from "../Lib/mixpanel";

const AddMember = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-button-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: box-shadow 120ms ease, transform 120ms ease, background-color 120ms ease;
  &:hover {
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: 3px solid #7c3aed;
    outline-offset: 2px;
  }
  &:active {
    transform: translateY(0);
  }
  @media(max-width: 768px){
    width: 24px;
    height: 24px;

  }
  img{
  width: 10px;
  }
`;

const roleOptions = [
  { value: "0", label: "Founding Team" },
  { value: "1", label: "Member" },
  { value: "2", label: "Intern/Volunteer" },
  { value: "3", label: "Consultant" },
  { value: "4", label: "Investor" },
  { value: "5", label: "Professor" },
];

export function InviteMembers() {
  const extractErrorMessage = (payload, fallback) => {
    if (!payload) return fallback;
    if (typeof payload.error === "string") return payload.error;
    if (Array.isArray(payload.errors)) return payload.errors.join(", ");
    if (typeof payload.message === "string") return payload.message;
    return fallback;
  };
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);
  const [roleOffered, setRoleOffered] = useState("");
  const [reason, setReason] = useState("");
  const [notification, setNotification] = useState(null);
  const [disableButton, setDisableButton] = useState(false);
  const { t } = useTranslation();
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const debounceRef = useRef(null);
  const [hoveredEmail, setHoveredEmail] = useState(null);
  const [focusedEmail, setFocusedEmail] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("invite_open")) open();
  }, [open, close]);

  // Allow opening the modal via a global event so triggers inside popovers/menus can open a persistent instance
  useEffect(() => {
    const handler = () => open();
    window.addEventListener('open-invite-members', handler);
    return () => window.removeEventListener('open-invite-members', handler);
  }, [open]);

  const { slug, isMember, isOwner, existingJoinRequest, id, members } =
    useStartupStore((state) => ({
      id: state.id,
      slug: state.slug,
      isOwner: state.isOwner,
      isMember: state.isMember,
      existingJoinRequest: state.existingJoinRequest,
      members: state.members,
    }));
  const currentUser = useUserStore((state) => state.user);

  let owner = null;
  let otherMembers = [];
  if (Array.isArray(members) && members.length > 0) {
    owner = members.find((m) => m.role && m.role.toLowerCase() === "owner");
    otherMembers = members.filter(
      (m) => !m.role || m.role.toLowerCase() !== "owner"
    );
  }
  // Add current user at the end if not already present
  let userList = [];
  if (owner) userList.push(owner);
  userList = userList.concat(otherMembers);
  if (currentUser && !userList.some((u) => u.email === currentUser.email)) {
    userList.push(currentUser);
  }

  const handleChange = (val) => {
    setValue(val);
    setData([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (!trimmed.length || trimmed.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`/api/users/search?search=${encodeURIComponent(trimmed)}`);
        let users = await response.json();

        // Fallback: if no users and looks like full name, try token searches and merge
        if ((!users || users.length === 0) && /\s+/.test(trimmed)) {
          const tokens = trimmed.split(/\s+/).filter(Boolean);
          if (tokens.length >= 2) {
            const uniqueMap = new Map();
            const tokenResponses = await Promise.all(
              tokens.slice(0, 2).map((tk) => fetch(`/api/users/search?search=${encodeURIComponent(tk)}`))
            );
            const tokenUsersArrays = await Promise.all(tokenResponses.map((r) => r.json()));
            tokenUsersArrays.flat().forEach((u) => uniqueMap.set(u.email, u));
            const merged = Array.from(uniqueMap.values());
            const qLower = trimmed.toLowerCase();
            users = merged.filter((u) => {
              const fn = `${u.first_name || ""}`.toLowerCase();
              const ln = `${u.last_name || ""}`.toLowerCase();
              const full = `${fn} ${ln}`.trim();
              return full.includes(qLower);
            });
          }
        }

        setData(users || []);
        if ((users == null || users.length === 0)) {
          Mixpanel.custom('Invite Search No Results', {
            query: trimmed,
            likely_email: /@/.test(trimmed),
            query_length: trimmed.length
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleOpen = () => {
    open();
    Mixpanel.modal('invite_members', 'opened', {
      startup_slug: slug,
      user_role: isOwner ? 'owner' : (isMember ? 'member' : 'visitor'),
      trigger_source: 'manual_click'
    });
  };
  

  const handleSubmit = async () => {
    if (selectedUser) {
      if (userList.some((u) => u.email === selectedUser.email)) {
        setNotification({
          type: "failure",
          content: "User is already a member of the team.",
        });
        return;
      }
    }

    const inviteStartTime = performance.now();

    try {
      setInviteSubmitting(true);
      const response = await fetchApi("/invitations", "POST", {
        email: selectedUser.email,
        inviteable_type: "StartupEntity",
        inviteable_id: id,
        role: selectedUser.role !== null ? Number(selectedUser.role) : 1
      });

      const inviteTime = performance.now() - inviteStartTime;

      if (response.ok) {
        const payload = await response.json().catch(() => ({}));
        const msg = payload?.message || "Invitation sent successfully.";
        setNotification({ type: "success", content: msg });
        Mixpanel.custom("Member Invitation Sent", {
          startup_slug: slug,
          invited_user_email: selectedUser.email,
          assigned_role: selectedUser.role,
          invitation_type: "existing_user",
          response_time: inviteTime,
          success: true,
        });
        setValue("");
        setData([]);
        setSelectedUser(null);
      } else {
        const payload = await response.json().catch(() => ({}));
        const msg = extractErrorMessage(payload, "Failed to send invitation.");
        setNotification({ type: "failure", content: msg });
        Mixpanel.error("member_invitation", {
          message: "Member invitation failed",
          startup_slug: slug,
          invited_user_email: selectedUser.email,
          assigned_role: selectedUser.role,
          invitation_type: "existing_user",
          response_time: inviteTime,
          error_message: msg,
        });
      }
    } catch (error) {
      const inviteTime = performance.now() - inviteStartTime;
      Mixpanel.error("member_invitation_exception", {
        message: "Error during member invitation",
        startup_slug: slug,
        response_time: inviteTime,
        error_message: error.message,
      });
      console.error("Error during Invitation", error);
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleInvite = async () => {
    const requestStartTime = performance.now();
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/join_requests/create_for_startup`,
        "POST",
        {
          join_request: { reason, role: roleOffered },
        }
      );

      const requestTime = performance.now() - requestStartTime;

      if (response.ok) {
        const payload = await response.json().catch(() => ({}));
        const msg = payload?.message || "Invitation sent successfully.";
        setNotification({ type: "success", content: msg });
        Mixpanel.custom('Join Request Sent', {
          startup_slug: slug,
          requested_role: roleOffered,
          reason_provided: !!reason,
          response_time: requestTime,
          success: true
        });
        setDisableButton(true);
        setSelectedUser(null);
        setValue("");
        setData([]);
        console.log("Request sent successful!");
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: msg });
        Mixpanel.error('join_request', {
          message: 'Join request failed',
          startup_slug: slug,
          requested_role: roleOffered,
          response_time: requestTime,
          error_message: msg
        });
      }
    } catch (error) {
      console.error("Error during Sending request", error);
      const requestTime = performance.now() - requestStartTime;
      Mixpanel.error('join_request_exception', {
        message: 'Error during join request',
        startup_slug: slug,
        response_time: requestTime,
        error_message: error.message
      });
    }
  };

  const sendUserInvite = async () => {
    const inviteStartTime = performance.now();
    try {
      setInviteSubmitting(true);
      const response = await fetchApi("/users/invitation", "POST", {
        user: { email: value },
        startup_id: id,
      });

      const inviteTime = performance.now() - inviteStartTime;

      if (response.ok) {
        const msg = (await response.json()).message;
        setNotification({ type: "success", content: msg });
        Mixpanel.custom("New User Invitation Sent", {
          startup_slug: slug,
          invited_email: value,
          invitation_type: "new_user",
          response_time: inviteTime,
          success: true,
        });
        setValue("");
        setData([]);
        console.log("Request sent successful!");
      } else {
        const payload = await response.json().catch(() => ({}));
        const msg = extractErrorMessage(payload, "Failed to send invitation.");
        setNotification({ type: "failure", content: msg });
        Mixpanel.error("new_user_invitation", {
          message: "New user invitation failed",
          startup_slug: slug,
          invited_email: value,
          response_time: inviteTime,
          error_message: msg,
        });
      }
    } catch (error) {
      const inviteTime = performance.now() - inviteStartTime;
      Mixpanel.error("new_user_invitation_exception", {
        message: "Error during new user invitation",
        startup_slug: slug,
        response_time: inviteTime,
        error_message: error.message,
      });
    } finally {
      setInviteSubmitting(false);
    }
  };
  const onInviteClick = () => {
    if (selectedUser) {
      handleSubmit();
    } else {
      sendUserInvite();
    }
  };

  const canInviteNewUser =
    !selectedUser && isValidEmail && !loading && data.length === 0;
  const isAlreadyMember =
    selectedUser && userList.some((u) => u.email === selectedUser.email);
  const canInviteSelectedUser = selectedUser && !isAlreadyMember;
  return (
    <>
      <Modal
        opened={opened}
        onClose={() => {
          setValue("");
          setNotification(null);
          setSelectedUser(null);
          close();
        }}
        size="lg"
        radius="md"
        transitionProps={{ transition: "fade", duration: 200 }}
        withCloseButton={true}
        title={
          <Group spacing={8} align="center">
            <Title order={4} style={{ lineHeight: 1.2 }}>{isOwner || isMember ? "Invite Members" : "Request to join"}</Title>
          </Group>
        }
        aria-labelledby="invite-members-title"
        trapFocus
        centered
      >
        {isOwner || isMember ? (
          <Stack spacing={18} className="p-5">
            {notification && (
              <Box role="alert" style={{ zIndex: 3 }}>
                <Notification {...notification} />
              </Box>
            )}
            <Title
              id="invite-members-title"
              order={5}
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              Invite Members
            </Title>
            <Text size="md" role="note" id="invite-helper-text">
              Enter a name or email to invite a user
            </Text>
            {/* Search bar or selected user summary */}
            {!selectedUser ? (
              <TextInput
                icon={<IconSearch />}
                variant="unstyled"
                placeholder={t("startups_page.invite.placeholder")}
                value={value}
                onChange={(event) => handleChange(event.currentTarget.value)}
                rightSection={loading ? <Loader size="1rem" /> : null}
                style={{
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: 4,
                  background: "white",
                  height: 40,
                  minHeight: 40,
                  fontSize: 16,
                }}
                w="100%"
                aria-label={t("startups_page.invite.placeholder")}
                aria-describedby="invite-helper-text"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (canInviteSelectedUser || canInviteNewUser)) {
                    onInviteClick();
                  }
                }}
              />
            ) : (
              <div
                style={{
                  position: "relative",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: 12,
                  padding: 16,
                  background: "var(--mantine-color-gray-0)",
                  minHeight: 64,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
                aria-live="polite"
              >
                <Group position="apart" align="center" noWrap>
                  <Group spacing="sm" noWrap>
                    <Avatar src={selectedUser.avatar_url} radius="xl" size="sm">
                      {!selectedUser.avatar_url &&
                        `${selectedUser.first_name?.[0] || ""}${
                          selectedUser.last_name?.[0] || ""
                        }`}
                    </Avatar>
                    <div>
                      <Text size="sm" weight={500}>
                        {selectedUser.first_name} {selectedUser.last_name}
                      </Text>
                      <Text color="dimmed" size="xs">
                        {selectedUser.email}
                      </Text>
                    </div>
                  </Group>
                  <Group spacing={8} align="center" noWrap>
                    <Select
                      data={roleOptions}
                      value={selectedUser.role}
                      onChange={(role) =>
                        setSelectedUser((prev) => ({ ...prev, role }))
                      }
                      withinPortal={true}
                      placeholder="Assign role"
                      style={{
                        minWidth: 160,
                        background: "white",
                        color: "black",
                      }}
                      styles={{ input: { background: "white", color: "black" } }}
                      dropdownPosition="bottom"
                      aria-label="Assign role to selected user"
                    />
                    <ActionIcon
                      variant="subtle"
                      size={28}
                      onClick={() => setSelectedUser(null)}
                      aria-label="Clear selected user"
                      title="Clear"
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </div>
            )}
            {/* Show search results if searching and no user is selected */}
            {!selectedUser && value.trim().length > 0 ? (
              <>
                <Box style={{ position: 'relative' }}>
                  <LoadingOverlay visible={loading} overlayOpacity={0.15} transitionDuration={150} zIndex={1} />
                  {value.trim().length < 3 ? (
                    <Box
                      p="md"
                      style={{
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: 8,
                        textAlign: "center",
                        background: "var(--mantine-color-gray-0)",
                        color: "var(--mantine-color-dimmed)",
                      }}
                      role="status"
                    >
                      <Text size="sm">Type at least 3 characters to search.</Text>
                    </Box>
                  ) : data.length > 0 ? (
                    <ScrollArea style={{ maxHeight: 220 }} type="auto" aria-label="Search results">
                      <Stack spacing={4} role="listbox" aria-label="Users">
                        {data
                          .filter(
                            (user) =>
                              user &&
                              (user.first_name || user.last_name || user.email)
                          )
                          .map((user) => (
                            <Box
                              key={user.email}
                              role="option"
                              aria-selected={false}
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedUser({ ...user, role: '1' });
                                }
                              }}
                              onClick={() => setSelectedUser({ ...user, role: '1' })}
                              onMouseEnter={() => setHoveredEmail(user.email)}
                              onMouseLeave={() => setHoveredEmail(null)}
                              onFocus={() => setFocusedEmail(user.email)}
                              onBlur={() => setFocusedEmail(null)}
                              onMouseDown={(e) => e.preventDefault()}
                              style={{
                                cursor: 'pointer',
                                borderRadius: 8,
                                padding: 8,
                                outline: 'none',
                                background: hoveredEmail === user.email || focusedEmail === user.email ? 'var(--mantine-color-violet-0)' : '#fff',
                                border: `1px solid ${hoveredEmail === user.email || focusedEmail === user.email ? 'var(--mantine-color-violet-3)' : '#eee'}`,
                                boxShadow: focusedEmail === user.email
                                  ? '0 0 0 2px var(--mantine-color-violet-5)'
                                  : (hoveredEmail === user.email ? '0 0 0 2px var(--mantine-color-violet-3)' : 'none'),
                                transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                              }}
                            >
                              <Group spacing="xs">
                                <Avatar src={user.avatar_url} radius="xl" size="sm">
                                  {!user.avatar_url &&
                                    `${user.first_name?.[0] || ""}${
                                      user.last_name?.[0] || ""
                                    }`}
                                </Avatar>
                                <div>
                                  <Text size="sm" weight={500}>
                                    {user.first_name} {user.last_name}
                                  </Text>
                                  <Text color="dimmed" size="xs">
                                    {user.email}
                                  </Text>
                                </div>
                              </Group>
                            </Box>
                          ))}
                      </Stack>
                    </ScrollArea>
                  ) : (
                    <Box
                      p="md"
                      style={{
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: 8,
                        background: "var(--mantine-color-gray-0)",
                      }}
                      role="status"
                    >
                      <Stack spacing={6} align="start">
                        <Text size="sm" weight={500}>No matching users on NowCollab.</Text>
                        {!isValidEmail ? (
                          <Text size="sm" color="dimmed">
                            Enter their complete email address to invite them.
                          </Text>
                        ) : (
                          <Text size="sm" color="dimmed">
                            Looks like an email. You can invite <Text component="span" weight={600}>{value.trim()}</Text> directly.
                          </Text>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </>
            ) : null}
            {/* Invite button at the bottom */}
            <Button
              onClick={onInviteClick}
              color={
                canInviteSelectedUser || canInviteNewUser ? "violet" : undefined
              }
              loading={inviteSubmitting}
              style={{
                marginTop: 16,
                minWidth: 100,
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 8,
                background:
                  canInviteSelectedUser || canInviteNewUser
                    ? undefined
                    : "var(--mantine-color-gray-1)",
                color:
                  canInviteSelectedUser || canInviteNewUser ? "white" : "var(--mantine-color-gray-6)",
                height: 32,
                minHeight: 32,
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: "var(--mantine-color-gray-3)",
                width: "100%",
              }}
              disabled={!canInviteSelectedUser && !canInviteNewUser}
              aria-disabled={!canInviteSelectedUser && !canInviteNewUser}
              aria-label={
                selectedUser
                  ? `Invite ${selectedUser.email} as ${selectedUser.role}`
                  : `Invite ${value}`
              }
              styles={{
                root: {
                  cursor: !canInviteSelectedUser && !canInviteNewUser ? 'not-allowed' : 'pointer',
                  opacity: !canInviteSelectedUser && !canInviteNewUser ? 0.4 : 1,
                  filter: !canInviteSelectedUser && !canInviteNewUser ? 'grayscale(0.3)' : 'none',
                },
              }}
            >
              Invite
            </Button>
            {isAlreadyMember && (
              <Text color="red" size="sm" mt={4}>
                This user is already a member of the team.
              </Text>
            )}
          </Stack>
        ) : (
          <Stack className="p-4">
            {notification && (
              <Box role="alert" style={{ zIndex: 3 }}>
                <Notification {...notification} />
              </Box>
            )}
            <Textarea
              label={t("startups_page.roles.m_a_reason")}
              placeholder="Hello..."
              autosize
              minRows={3}
              maxRows={5}
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
              aria-label={t("startups_page.roles.m_a_reason")}
              aria-required="true"
            />
            <Select
              label={t("startups_page.roles.m_s_title")}
              data={[
                "Founding Team",
                "Contributor",
                "Intern/Volunteer",
                "Consultant",
                "Investor",
              ]}
              value={roleOffered}
              description={t("startups_page.roles.m_s_desc")}
              placeholder="e.g., Co-founder"
              onChange={setRoleOffered}
              withinPortal={true}
              aria-label={t("startups_page.roles.m_s_title")}
            />
            {!notification ? (
              <Button
                onClick={handleInvite}
                variant="default"
                fullWidth
                className="w-full px-12"
                disabled={!reason}
                aria-disabled={!reason}
                aria-label="Send join request"
              >
                {t("startups_page.roles.m_a_request")}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setNotification(null);
                  close();
                }}
                variant="default"
                fullWidth
                className="w-full px-12"
                disabled={!reason}
                aria-label="Close join request modal"
              >
                {t("startups_page.roles.m_a_close")}
              </Button>
            )}
          </Stack>
        )}
      </Modal>
      <>
        {isOwner || isMember ? (
          <AddMember onClick={handleOpen} aria-label="Open invite members modal">
            <img src={plusIcon} alt="Add Member" />
          </AddMember>
        ) : (
          <UnstyledButton onClick={handleOpen} aria-label="Request to join startup">
            <Group>
              <ActionIcon variant="subtle">
                <IconHandGrab size={24} />
              </ActionIcon>
              <p style={{ fontSize: "16px" }}>
                {`${
                  disableButton || existingJoinRequest
                    ? t("startups_page.members.request_sent")
                    : t("startups_page.members.request_join")
                }`}
              </p>
            </Group>
          </UnstyledButton>
        )}
      </>
    </>
  );
}
